import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { pick } from 'ramda';
import { useJackpotsSelector } from 'js/store/hooks';
import { componentService } from 'js/services/component.service';

import * as hooks from '@core/js/components/common/Jackpot/hooks';
import * as Types from './types';
import * as helpers from '@core/js/components/common/Jackpot/helpers';

export const Jackpot: React.FC<Types.IJackpotProps> = ({
    code,
    interval = 100,
    className = '',
    enableHitTime = true,
    jackpotCard = false, /* TotalCasino changes */
    jackpotCardDefaultData /* TotalCasino changes */
}) => {
    const animationFrame = useRef<number>(0);
    const jackpotRef = useRef<HTMLSpanElement>(null);
    const {
        jackpots, totalCasinoJackpot, jackpotStartTime
    } = useJackpotsSelector(pick(['jackpots', 'totalCasinoJackpot', 'jackpotStartTime']));
    /* TotalCasino changes */
    const jackpot = useMemo(() => {
        if (code && jackpots[code]) {
            return jackpots[code];
        }

        if (jackpotCard) {
            return jackpotCardDefaultData;
        }

        return totalCasinoJackpot;
    }, [code, jackpots, jackpotCard, jackpotCardDefaultData, totalCasinoJackpot]);
    /* TotalCasino changes */

    const updateTickerValue = hooks.useUpdateTickerValue();

    const updateAnimation = useCallback((tickerData: Types.IJackpotTickerData) => {
        animationFrame.current && cancelAnimationFrame(animationFrame.current);

        if (!tickerData.isLimitReached || !tickerData.isHitTimeEnded) {
            animationFrame.current = requestAnimationFrame(() => updateAnimation(tickerData));
        }

        updateTickerValue({ enableHitTime, interval, jackpotRef, tickerData });
    }, [updateTickerValue, enableHitTime, interval]);

    useEffect(() => {
        if (jackpot) {
            updateAnimation(helpers.getInitialTickerData(jackpotStartTime, jackpot));
        }

        return () => cancelAnimationFrame(animationFrame.current);
    }, [jackpot, updateAnimation, jackpotStartTime]);

    if (!jackpot) {
        return null;
    }

    return (
        <span
            ref={jackpotRef}
            className={className}
            data-jackpot-code={code || ''}
        />
    );
};

/**
 * "Jackpot" (<hub2-jackpot code="jackpot_code"></hub2-jackpot>) is a regular component that shows a jackpot per code.
 * "Jackpots (<hub2-jackpots></hub2-jackpots>)" is a component that shows Total Casino Jackpot.
 */
componentService.registerWebContentComponents({ Jackpot, Jackpots: Jackpot });
